import { API_BASE, authHeaders } from './apiAuth'
import { decodeRefs, type ImageRef } from './imageRefs'

/**
 * Amazon Carousel, from the page's side.
 *
 * Everything goes through the Worker, which holds the Rainforest key, keeps
 * each answer for a week and writes down what each request cost. Nothing here
 * talks to Amazon or to Rainforest directly -- except that the pictures
 * themselves are shown straight from Amazon's image host, which costs nothing.
 */

export type Market = 'us' | 'uk' | 'ca' | 'mx' | 'br' | 'au' | 'jp' | 'in' | 'sg' | 'eg'

/** In the order the menu shows them. The Worker holds the same list. */
export const MARKETS: { value: Market; label: string; domain: string }[] = [
  { value: 'us', label: 'United States', domain: 'amazon.com' },
  { value: 'uk', label: 'United Kingdom', domain: 'amazon.co.uk' },
  { value: 'ca', label: 'Canada', domain: 'amazon.ca' },
  { value: 'mx', label: 'Mexico', domain: 'amazon.com.mx' },
  { value: 'br', label: 'Brazil', domain: 'amazon.com.br' },
  { value: 'au', label: 'Australia', domain: 'amazon.com.au' },
  { value: 'jp', label: 'Japan', domain: 'amazon.co.jp' },
  { value: 'in', label: 'India', domain: 'amazon.in' },
  // Also where Malaysia shops: there is no Amazon of Malaysia's own.
  { value: 'sg', label: 'Singapore', domain: 'amazon.sg' },
  // Added 2026-10-06. Vietnam was asked for too, but Amazon has no store there.
  { value: 'eg', label: 'Egypt', domain: 'amazon.eg' },
]

export const marketOf = (value: string): (typeof MARKETS)[number] =>
  MARKETS.find((one) => one.value === value) ?? MARKETS[0]

export interface FoundProduct {
  asin: string
  title: string
  /** Empty until somebody has opened the product: a search carries none. */
  brand: string
  /** Amazon's 320 px thumbnail. */
  image: string
  /**
   * Its product line -- brand and kind of product, flavour and size left out
   * -- as the server worked it out. Empty: a line of its own.
   */
  family?: string
}

export interface FoundPage {
  results: FoundProduct[]
  page: number
  totalPages: number
  /** Read from the week's cache, so it cost nothing. */
  cached: boolean
}

export interface ProductPicture {
  url: string
  kind: 'main' | 'gallery' | 'aplus'
  name: string
}

export interface OpenedProduct {
  asin: string
  title: string
  brand: string
  pictures: ProductPicture[]
  cached: boolean
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    return body.error || `Request failed (${response.status})`
  } catch {
    return `Request failed (${response.status})`
  }
}

/** A refusal that says how: 429 is "too many at once", not a broken search. */
export class AmazonError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

export async function searchAmazon(market: Market, term: string, page = 1): Promise<FoundPage> {
  const query = new URLSearchParams({ market, q: term, page: String(page) })
  const response = await fetch(`${API_BASE}/api/amazon/search?${query}`, {
    // Amazon is slow at times, never this slow: past a minute, say so and let go.
    signal: AbortSignal.timeout(60_000),
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new AmazonError(await reason(response), response.status)
  const body = (await response.json()) as Partial<FoundPage>
  return {
    results: Array.isArray(body.results) ? body.results : [],
    page: Number(body.page) || page,
    totalPages: Number(body.totalPages) || 1,
    cached: body.cached === true,
  }
}

export async function openProduct(
  market: Market,
  asin: string,
  signal?: AbortSignal,
): Promise<OpenedProduct> {
  const query = new URLSearchParams({ market, asin })
  const response = await fetch(`${API_BASE}/api/amazon/product?${query}`, {
    headers: { Accept: 'application/json', ...(await authHeaders()) },
    signal,
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Partial<OpenedProduct>
  return {
    asin: String(body.asin ?? asin),
    title: String(body.title ?? ''),
    brand: String(body.brand ?? ''),
    pictures: Array.isArray(body.pictures) ? body.pictures : [],
    cached: body.cached === true,
  }
}

/** One picked picture, and the listing it came from. */
export interface Picked {
  url: string
  asin: string
  title: string
}

/** The Worker takes this many per request. */
const KEEP_BATCH = 40

/**
 * Store the picked pictures as this person's own files, and say where.
 *
 * In batches, because each picture is fetched from Amazon and uploaded by the
 * Worker, and a request has a time limit. What comes back names each kept
 * picture the way the Miro queue and the canvas name pictures.
 */
export async function keepPictures(
  market: Market,
  picked: Picked[],
  onProgress?: (done: number) => void,
): Promise<{ refs: ImageRef[]; failed: number }> {
  const refs: ImageRef[] = []
  let failed = 0
  for (let start = 0; start < picked.length; start += KEEP_BATCH) {
    const batch = picked.slice(start, start + KEEP_BATCH)
    const response = await fetch(`${API_BASE}/api/amazon/keep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ market, pictures: batch }),
    })
    if (!response.ok) throw new Error(await reason(response))
    const body = (await response.json()) as { refs?: unknown; failed?: unknown[] }
    refs.push(...decodeRefs(body.refs))
    failed += Array.isArray(body.failed) ? body.failed.length : 0
    onProgress?.(Math.min(picked.length, start + batch.length))
  }
  return { refs, failed }
}


/**
 * Amazon pictures kept straight into one project ("Copy to project").
 *
 * Answers in the order asked: the new file's id, or null for a picture Amazon
 * would not send. A board waiting on placeholders fills each from its own
 * slot. The project must be the caller's; the Worker checks.
 */
export async function keepIntoProject(
  projectId: string,
  market: Market,
  picked: Picked[],
): Promise<(string | null)[]> {
  const out: (string | null)[] = []
  for (let start = 0; start < picked.length; start += KEEP_BATCH) {
    const batch = picked.slice(start, start + KEEP_BATCH)
    const response = await fetch(`${API_BASE}/api/amazon/keep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ market, pictures: batch, project: projectId }),
    })
    if (!response.ok) throw new Error(await reason(response))
    const body = (await response.json()) as { slots?: unknown[] }
    const slots = Array.isArray(body.slots) ? body.slots : []
    batch.forEach((_, index) => {
      const ref = decodeRefs([slots[index]])[0]
      out.push(ref && ref.store === 'project' ? ref.id : null)
    })
  }
  return out
}

/**
 * A picture's own name, without the size Amazon was asked to draw it at.
 *
 * The same picture appears at 320 px in a search and at full size on the
 * product, under two addresses. This is what they have in common -- which is
 * what lets a main picture ticked on a card and the same picture ticked
 * inside the product be one tick.
 */
export function pictureKey(url: string): string {
  const path = url.split('?')[0]
  const cut = path.lastIndexOf('/')
  return path.slice(0, cut + 1) + path.slice(cut + 1).split('.')[0]
}

/** The picture at the size it was uploaded, for looking at closely. */
export function fullSize(url: string): string {
  const extension = (url.split('?')[0].match(/\.(jpe?g|png|gif)$/i) ?? [])[1] ?? 'jpg'
  return `${pictureKey(url)}.${extension.toLowerCase()}`
}
